"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IdentityImportRequestSchema = exports.IdentityRelinkRequestSchema = exports.IdentityRetentionRequestSchema = exports.IdentityCreateCheckRequestSchema = exports.IdentityEventSchema = exports.IdentityAuditEventSchema = exports.IdentitySensitiveSchema = exports.IdentityCheckViewSchema = exports.IdentitySummarySchema = exports.IDENTITY_OPEN_STATUSES = exports.IdentityCheckStatusSchema = exports.IDENTITY_STATUSES = exports.IdentitySubjectSchema = exports.IDENTITY_ACTION_IDS = exports.IDENTITY_FEATURES = exports.IDENTITY_CONSENT_VERSION = void 0;
// Identity verification + vault contracts (spec docs/superpowers/specs/2026-10-15-identity-verification-design.md).
// Additive only. IdentityCheckView is the ONLY shape any API returns for a check -- it has no
// sensitive field; z.object() strips unknown keys, so a stray dob/ssn can never leak through it.
const zod_1 = require("zod");
const isoDate = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const text = (max) => zod_1.z.string().trim().max(max);
exports.IDENTITY_CONSENT_VERSION = '2026-10-15';
exports.IDENTITY_FEATURES = {
    verify: 'identity.verify', ssn: 'identity.ssn', vault: 'identity.vault', export: 'identity.export',
    import: 'identity.import', legalHold: 'identity.legal_hold', age: 'identity.age',
};
exports.IDENTITY_ACTION_IDS = [
    'identity.document_check', 'identity.ssn_check', 'identity.export', 'identity.import', 'identity.storage', 'identity.age_check',
];
exports.IdentitySubjectSchema = zod_1.z.object({
    app: zod_1.z.enum(['estate', 'employee', 'admin_staff', 'storefront']),
    type: zod_1.z.string().min(1).max(40).regex(/^[a-z_]+$/),
    id: zod_1.z.string().min(1).max(80).regex(/^[A-Za-z0-9_\-:.]+$/),
});
exports.IDENTITY_STATUSES = ['link_sent', 'in_progress', 'processing', 'requires_input', 'verified', 'failed', 'canceled', 'expired', 'imported', 'purged'];
exports.IdentityCheckStatusSchema = zod_1.z.enum(exports.IDENTITY_STATUSES);
exports.IDENTITY_OPEN_STATUSES = ['link_sent', 'in_progress', 'processing', 'requires_input'];
const Actor = zod_1.z.object({ sub: zod_1.z.string().max(80), name: text(160).default('') });
exports.IdentitySummarySchema = zod_1.z.object({
    idType: zod_1.z.enum(['driving_license', 'passport', 'id_card', 'other']),
    issuingCountry: text(2).default(''),
    issuingState: text(10).optional(),
    idExpiresOn: isoDate.optional(),
    idNumberLast4: zod_1.z.string().max(4).optional(),
    ssnLast4: zod_1.z.string().max(4).optional(),
    selfieMatched: zod_1.z.boolean().default(false),
    nameOnId: text(80).default(''), // "Jane S." only
});
exports.IdentityCheckViewSchema = zod_1.z.object({
    checkId: zod_1.z.string(), tenantId: zod_1.z.string(), subject: exports.IdentitySubjectSchema,
    source: zod_1.z.enum(['stripe', 'import']), status: exports.IdentityCheckStatusSchema,
    options: zod_1.z.object({ ssn: zod_1.z.boolean().default(false) }).default({}),
    createdAt: zod_1.z.string(), updatedAt: zod_1.z.string(), createdBy: Actor,
    consentAt: zod_1.z.string().optional(), consentVersion: zod_1.z.string().optional(), verifiedAt: zod_1.z.string().optional(),
    failureReason: zod_1.z.enum(['document_unverified', 'selfie_mismatch', 'id_number_mismatch', 'other']).optional(),
    summary: exports.IdentitySummarySchema.optional(),
    nameMatch: zod_1.z.enum(['match', 'mismatch', 'unknown']).optional(),
    retainUntil: isoDate.optional(), legalHold: zod_1.z.boolean().default(false), purgedAt: zod_1.z.string().optional(),
    linkExpiresAt: zod_1.z.string().optional(),
});
exports.IdentitySensitiveSchema = zod_1.z.object({
    fullName: text(200), dob: isoDate.optional(),
    address: zod_1.z.object({ line1: text(200).default(''), line2: text(200).default(''), city: text(100).default(''),
        state: text(50).default(''), postalCode: text(20).default(''), country: text(2).default('') }).optional(),
    idNumber: text(60).optional(), ssn: text(20).optional(),
    images: zod_1.z.object({ front: zod_1.z.string().optional(), back: zod_1.z.string().optional(), selfie: zod_1.z.string().optional() }).default({}),
});
exports.IdentityAuditEventSchema = zod_1.z.object({
    at: zod_1.z.string(), checkId: zod_1.z.string(), actor: Actor.extend({ role: zod_1.z.string().max(40).default('') }),
    action: zod_1.z.enum(['requested', 'consented', 'verified', 'failed', 'viewed', 'exported', 'imported', 'hold_on', 'hold_off', 'retention_set', 'purged', 'canceled', 'relinked']),
    reason: text(300).optional(), ip: zod_1.z.string().max(60).optional(),
});
exports.IdentityEventSchema = zod_1.z.object({
    type: zod_1.z.enum(['identity.verified', 'identity.failed', 'identity.requires_input', 'identity.purged']),
    tenantId: zod_1.z.string(), checkId: zod_1.z.string(), subject: exports.IdentitySubjectSchema, at: zod_1.z.string(),
});
exports.IdentityCreateCheckRequestSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1), subject: exports.IdentitySubjectSchema, expectedName: text(200).min(1),
    email: zod_1.z.string().trim().toLowerCase().email().max(254),
    options: zod_1.z.object({ ssn: zod_1.z.boolean().default(false) }).default({}),
    actor: Actor, linkBase: zod_1.z.string().regex(/^https:\/\/[^\s"'<>]+$/).max(500),
    defaultRetainMonths: zod_1.z.number().int().min(1).max(120).optional(),
    billing: zod_1.z.enum(['tenant', 'internal']).default('tenant'),
});
exports.IdentityRetentionRequestSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1), subject: exports.IdentitySubjectSchema, retainUntil: isoDate.nullable(),
});
exports.IdentityRelinkRequestSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1), from: exports.IdentitySubjectSchema, to: exports.IdentitySubjectSchema,
});
exports.IdentityImportRequestSchema = zod_1.z.object({
    subject: exports.IdentitySubjectSchema, uploadId: zod_1.z.string().min(1).max(80),
    details: exports.IdentitySensitiveSchema.omit({ images: true }).extend({
        idType: zod_1.z.enum(['driving_license', 'passport', 'id_card', 'other']), issuingState: text(10).optional(), idExpiresOn: isoDate.optional(),
    }),
    files: zod_1.z.array(zod_1.z.enum(['front', 'back', 'selfie'])).min(1).max(3),
});
//# sourceMappingURL=identity.js.map